import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { updateProfile, changePassword } from "../../store/thunks/authThunks";
import Register from "./Register";

const Profile = () => {
  const dispatch = useDispatch();
  const { admin, loading, error } = useSelector((state) => state.auth);

  const [activeTab, setActiveTab] = useState("profile");
  const [profileData, setProfileData] = useState({
    fullName: "",
    email: "",
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [profileErrors, setProfileErrors] = useState({});
  const [passwordErrors, setPasswordErrors] = useState({});
  const [profileSuccess, setProfileSuccess] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");

  useEffect(() => {
    if (admin) {
      setProfileData({
        fullName: admin.fullName || "",
        email: admin.email || "",
      });
    }
  }, [admin]);

  const handleProfileChange = (e) => {
    setProfileData({
      ...profileData,
      [e.target.name]: e.target.value,
    });

    // Clear error when user starts typing
    if (profileErrors[e.target.name]) {
      setProfileErrors({
        ...profileErrors,
        [e.target.name]: "",
      });
    }

    // Clear success message
    if (profileSuccess) {
      setProfileSuccess("");
    }
  };

  const handlePasswordChange = (e) => {
    setPasswordData({
      ...passwordData,
      [e.target.name]: e.target.value,
    });

    // Clear error when user starts typing
    if (passwordErrors[e.target.name]) {
      setPasswordErrors({
        ...passwordErrors,
        [e.target.name]: "",
      });
    }

    // Clear success message
    if (passwordSuccess) {
      setPasswordSuccess("");
    }
  };

  const validateProfile = () => {
    const errors = {};

    if (!profileData.fullName) {
      errors.fullName = "Full name is required";
    }

    if (!profileData.email) {
      errors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(profileData.email)) {
      errors.email = "Email is invalid";
    }

    setProfileErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validatePassword = () => {
    const errors = {};

    if (!passwordData.currentPassword) {
      errors.currentPassword = "Current password is required";
    }

    if (!passwordData.newPassword) {
      errors.newPassword = "New password is required";
    } else if (passwordData.newPassword.length < 6) {
      errors.newPassword = "Password must be at least 6 characters";
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      errors.confirmPassword = "Passwords do not match";
    }

    setPasswordErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();

    if (!validateProfile()) return;

    try {
      await dispatch(updateProfile(profileData));
      setProfileSuccess("Profile updated successfully");
    } catch (error) {
      console.error("Profile update error:", error);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    if (!validatePassword()) return;

    try {
      await dispatch(
        changePassword({
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword,
        })
      );

      // Reset password fields
      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });

      setPasswordSuccess("Password changed successfully");
    } catch (error) {
      console.error("Password change error:", error);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6 mt-16">
      <h1 className="text-2xl font-bold text-[#B85C38] mb-6">My Profile</h1>

      <div className="mb-6 border-b">
        <div className="flex">
          <button
            className={`py-2 px-4 font-medium ${
              activeTab === "profile"
                ? "text-[#B85C38] border-b-2 border-[#B85C38]"
                : "text-gray-500 hover:text-[#B85C38]"
            }`}
            onClick={() => setActiveTab("profile")}
          >
            Profile Information
          </button>
          <button
            className={`py-2 px-4 font-medium ${
              activeTab === "password"
                ? "text-[#B85C38] border-b-2 border-[#B85C38]"
                : "text-gray-500 hover:text-[#B85C38]"
            }`}
            onClick={() => setActiveTab("password")}
          >
            Change Password
          </button>
          {admin.role === "admin" && (
            <button
              className={`py-2 px-4 font-medium ${
                activeTab === "register"
                  ? "text-[#B85C38] border-b-2 border-[#B85C38]"
                  : "text-gray-500 hover:text-[#B85C38]"
              }`}
              onClick={() => setActiveTab("register")}
            >
              Add User
            </button>
          )}
        </div>
      </div>

      {activeTab === "profile" && (
        <div>
          {error && (
            <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
              {error}
            </div>
          )}

          {profileSuccess && (
            <div className="mb-4 p-3 bg-green-100 text-green-700 rounded-md">
              {profileSuccess}
            </div>
          )}

          <form onSubmit={handleProfileSubmit}>
            <div className="mb-4">
              <label
                htmlFor="fullName"
                className="block text-gray-700 font-medium mb-2"
              >
                Full Name
              </label>
              <input
                type="text"
                id="fullName"
                name="fullName"
                value={profileData.fullName}
                onChange={handleProfileChange}
                className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#B85C38] ${
                  profileErrors.fullName ? "border-red-500" : "border-gray-300"
                }`}
              />
              {profileErrors.fullName && (
                <p className="mt-1 text-red-500 text-sm">
                  {profileErrors.fullName}
                </p>
              )}
            </div>

            <div className="mb-6">
              <label
                htmlFor="email"
                className="block text-gray-700 font-medium mb-2"
              >
                Email Address
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={profileData.email}
                onChange={handleProfileChange}
                className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#B85C38] ${
                  profileErrors.email ? "border-red-500" : "border-gray-300"
                }`}
              />
              {profileErrors.email && (
                <p className="mt-1 text-red-500 text-sm">
                  {profileErrors.email}
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="bg-[#B85C38] text-white py-2 px-6 rounded-md hover:bg-[#B85C38]/90 focus:outline-none focus:ring-2 focus:ring-[#B85C38] focus:ring-opacity-50 transition-colors disabled:opacity-70"
            >
              {loading ? "Saving..." : "Save Changes"}
            </button>
          </form>
        </div>
      )}

      {activeTab === "password" && (
        <div>
          {error && (
            <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
              {error}
            </div>
          )}

          {passwordSuccess && (
            <div className="mb-4 p-3 bg-green-100 text-green-700 rounded-md">
              {passwordSuccess}
            </div>
          )}

          <form onSubmit={handlePasswordSubmit}>
            <div className="mb-4">
              <label
                htmlFor="currentPassword"
                className="block text-gray-700 font-medium mb-2"
              >
                Current Password
              </label>
              <input
                type="password"
                id="currentPassword"
                name="currentPassword"
                value={passwordData.currentPassword}
                onChange={handlePasswordChange}
                className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#B85C38] ${
                  passwordErrors.currentPassword
                    ? "border-red-500"
                    : "border-gray-300"
                }`}
              />
              {passwordErrors.currentPassword && (
                <p className="mt-1 text-red-500 text-sm">
                  {passwordErrors.currentPassword}
                </p>
              )}
            </div>

            <div className="mb-4">
              <label
                htmlFor="newPassword"
                className="block text-gray-700 font-medium mb-2"
              >
                New Password
              </label>
              <input
                type="password"
                id="newPassword"
                name="newPassword"
                value={passwordData.newPassword}
                onChange={handlePasswordChange}
                className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#B85C38] ${
                  passwordErrors.newPassword
                    ? "border-red-500"
                    : "border-gray-300"
                }`}
              />
              {passwordErrors.newPassword && (
                <p className="mt-1 text-red-500 text-sm">
                  {passwordErrors.newPassword}
                </p>
              )}
            </div>

            <div className="mb-6">
              <label
                htmlFor="confirmPassword"
                className="block text-gray-700 font-medium mb-2"
              >
                Confirm New Password
              </label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={passwordData.confirmPassword}
                onChange={handlePasswordChange}
                className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#B85C38] ${
                  passwordErrors.confirmPassword
                    ? "border-red-500"
                    : "border-gray-300"
                }`}
              />
              {passwordErrors.confirmPassword && (
                <p className="mt-1 text-red-500 text-sm">
                  {passwordErrors.confirmPassword}
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="bg-[#B85C38] text-white py-2 px-6 rounded-md hover:bg-[#B85C38]/90 focus:outline-none focus:ring-2 focus:ring-[#B85C38] focus:ring-opacity-50 transition-colors disabled:opacity-70"
            >
              {loading ? "Changing..." : "Change Password"}
            </button>
          </form>
        </div>
      )}
      {activeTab === "register" && <Register />}
    </div>
  );
};

export default Profile;
